/**
 * AI Consent Modal
 * Displays third-party AI disclosure and captures user consent
 *
 * Required by Apple App Store Guideline 5.1.2(i):
 * "You must clearly disclose where personal data will be shared with third parties,
 * including with third-party AI, and obtain explicit permission before doing so."
 */

import React, { useState } from "react";
import { ScrollView, Linking, TouchableOpacity, ActivityIndicator, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  Box,
  VStack,
  Text,
} from "@gluestack-ui/themed";
import { colors, FS_XS, FS_SM, FS_MD } from "../theme";
import { AI_PROVIDERS, translateProvider } from "../hooks/useAIConsent";
import { getPrivacyPolicyUrl } from "../config/legalUrls";
import { useDisplayTheme } from "../hooks/useDisplayTheme";
import { BottomSheetModal } from "./ui/BottomSheetModal";

const FS_HEADING = 22;
const BASE_BUTTON_HEIGHT = 48;

interface AIConsentModalProps {
  visible: boolean;
  onAccept: () => Promise<void>;
  onDecline: () => void;
}

interface AIConsentModalContentProps {
  onAccept: () => Promise<void>;
  onDecline: () => void;
  /** Renders without modal backdrop/constraints — for use inside Storybook */
  embedded?: boolean;
}

/**
 * Inner content of the AI consent modal.
 * Exported separately so Storybook can render it without a native Modal overlay.
 */
export function AIConsentModalContent({
  onAccept,
  onDecline,
  embedded = false,
}: AIConsentModalContentProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const [isAccepting, setIsAccepting] = useState(false);

  const buttonHeight = Math.round(BASE_BUTTON_HEIGHT * fontScale);

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await onAccept();
    } finally {
      setIsAccepting(false);
    }
  };

  const openPrivacyPolicy = () => {
    Linking.openURL(getPrivacyPolicyUrl());
  };

  // Inner content only. Card chrome (rounded top corners, backdrop,
  // safe-area-aware padding) is delegated to `BottomSheetModal` —
  // see its docstring for why this shared component exists (Android
  // gesture-zone overlap bug surfaced in run
  // 2026-06-01T11-04-27-091Z).
  const inner = (
    <>
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack space="lg">
          {/* Header */}
          <VStack space="xs" alignItems="center">
            <Text
              allowFontScaling={false}
              accessibilityRole="header"
              style={{ fontSize: FS_HEADING * fontScale, fontWeight: 'bold', color: theme.text, textAlign: 'center', ...ffs }}
            >
              {t('chat.consent.title')}
            </Text>
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, textAlign: 'center', ...ffs }}>
              {t('chat.consent.subtitle')}
            </Text>
          </VStack>

          <View style={{ height: 1, backgroundColor: theme.border }} />

          {/* Explanation */}
          <VStack space="sm">
            <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.text, ...ffs }}>
              {t('chat.consent.explanation')}
            </Text>
          </VStack>

          {/* Provider Cards */}
          <VStack space="md">
            {AI_PROVIDERS.map((providerId) => {
              const translated = translateProvider(t, providerId);
              return (
                <Box
                  key={providerId}
                  style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border }}
                  p="$4"
                  rounded="$lg"
                >
                  <VStack space="xs">
                    <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.text, ...ffs }}>
                      {translated.name}
                    </Text>
                    <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                      <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}>{t('chat.consent.purposeLabel')}</Text>
                      {translated.purpose}
                    </Text>
                    <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                      <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.textSecondary, ...ffs }}>{t('chat.consent.dataSharedLabel')}</Text>
                      {translated.dataShared}
                    </Text>
                  </VStack>
                </Box>
              );
            })}
          </VStack>

          {/* Important Notes */}
          <Box
            style={{ backgroundColor: colors.warning, opacity: 0.9 }}
            p="$4"
            rounded="$lg"
          >
            <VStack space="xs">
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, fontWeight: 'bold', color: 'white', ...ffs }}>
                {t('chat.consent.importantInfoTitle')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>
                {"\u2022"} {t('chat.consent.voiceRecordingNote')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>
                {"\u2022"} {t('chat.consent.textAnalysisNote')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: 'white', ...ffs }}>
                {"\u2022"} {t('chat.consent.withdrawConsentNote')}
              </Text>
            </VStack>
          </Box>

          {/* Privacy Policy Link */}
          <TouchableOpacity
            testID="ai-consent-link-privacy"
            accessibilityLabel={t('chat.consent.privacyPolicyLink')}
            accessibilityRole="link"
            onPress={openPrivacyPolicy}
          >
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.primary, textAlign: 'center', ...ffs }}>
              {t('chat.consent.privacyPolicyLink')}
            </Text>
          </TouchableOpacity>

          <View style={{ height: 1, backgroundColor: theme.border }} />

          {/* Action Buttons */}
          <VStack space="sm">
            <TouchableOpacity
              testID="ai-consent-accept"
              accessibilityLabel={t('chat.consent.acceptButton')}
              accessibilityRole="button"
              accessibilityState={{ disabled: isAccepting }}
              onPress={handleAccept}
              disabled={isAccepting}
              activeOpacity={0.7}
              style={{
                minHeight: buttonHeight,
                borderRadius: buttonHeight / 2,
                backgroundColor: colors.primary,
                justifyContent: 'center',
                alignItems: 'center',
                opacity: isAccepting ? 0.5 : 1,
              }}
            >
              {isAccepting ? (
                <ActivityIndicator color="white" />
              ) : (
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: 'white', ...ffs }}>
                  {t('chat.consent.acceptButton')}
                </Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              testID="ai-consent-decline"
              accessibilityLabel={t('chat.consent.declineButton')}
              accessibilityRole="button"
              accessibilityState={{ disabled: isAccepting }}
              onPress={onDecline}
              disabled={isAccepting}
              activeOpacity={0.7}
              style={{
                minHeight: buttonHeight,
                borderRadius: buttonHeight / 2,
                borderWidth: 1,
                borderColor: theme.border,
                justifyContent: 'center',
                alignItems: 'center',
                opacity: isAccepting ? 0.5 : 1,
              }}
            >
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('chat.consent.declineButton')}
              </Text>
            </TouchableOpacity>

            <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, textAlign: 'center', marginTop: 8, ...ffs }}>
              {t('chat.consent.disclaimerText')}
            </Text>
          </VStack>
        </VStack>
      </ScrollView>
    </>
  );

  // Storybook entry: render the inner content inside an embedded
  // (non-Modal) BottomSheetModal so the card chrome is visible
  // without needing a Modal overlay.
  if (embedded) {
    return <BottomSheetModal embedded testID="ai-consent-modal-card">{inner}</BottomSheetModal>;
  }

  return inner;
}

export function AIConsentModal({
  visible,
  onAccept,
  onDecline,
}: AIConsentModalProps) {
  return (
    <BottomSheetModal
      visible={visible}
      onRequestClose={onDecline}
      testID="ai-consent-modal-card"
    >
      <AIConsentModalContent onAccept={onAccept} onDecline={onDecline} />
    </BottomSheetModal>
  );
}
